import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, FormError } from '@/components/app/common';
import { Logo } from '@/components/app/Logo';
import { api, post } from '@/lib/api';
import { keys } from '@/lib/queries';
import type { User } from '@/lib/types';

const STEPS = [
  ['Create a calendar', 'Add events — one-off, all-day or repeating.'],
  ['Include other calendars', 'Pull in the school calendar or public holidays. Their changes flow through automatically.'],
  ['Share one link', 'Parents tap once on iPhone, Google or Outlook. No app, no account.'],
];

export function Landing() {
  const qc = useQueryClient();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [k]: e.target.value });
    setErr('');
  };

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return setErr('Please enter a valid email address.');
    if (!form.password) return setErr('Please enter your password.');
    if (mode === 'register' && form.password.length < 8) return setErr('Password must be at least 8 characters.');
    setBusy(true);
    try {
      await post(`/auth/${mode}`, form);
      qc.setQueryData(keys.me, (await api<{ user: User }>('/me')).user);
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-svh">
      <header className="border-b">
        <div className="mx-auto flex h-14 max-w-6xl items-center gap-2.5 px-4 font-semibold tracking-tight md:px-8">
          <Logo /> Calendar Feed
        </div>
      </header>
      <main className="mx-auto grid grid-cols-[minmax(0,1fr)] max-w-6xl items-start gap-10 px-4 py-10 md:grid-cols-[1fr_400px] md:px-8 md:py-16">
        <section className="grid grid-cols-[minmax(0,1fr)] gap-6">
          <p className="text-primary text-xs font-semibold tracking-[0.12em] uppercase">Composable calendar feeds</p>
          <h1 className="max-w-xl text-4xl font-semibold tracking-tight text-balance md:text-5xl">One link. Every calendar that matters.</h1>
          <p className="text-muted-foreground max-w-xl text-lg">Schools, teachers, coaches and families publish calendars that build on each other. We merge them, remove duplicates, and keep every subscriber in sync — automatically.</p>
          <div className="flex flex-wrap items-center gap-2 text-sm" role="img" aria-label="Public holidays flow into the school calendar, then the class calendar, then your calendar">
            {['Public holidays', 'School', 'Class', 'Your calendar'].map((s, i) => (
              <span key={s} className="flex items-center gap-2">
                {i ? <ArrowRight className="text-muted-foreground size-4" /> : null}
                <span className={i === 3 ? 'bg-foreground text-background rounded-full px-3 py-1 font-medium' : 'bg-card rounded-full border px-3 py-1'}>{s}</span>
              </span>
            ))}
          </div>
          <ol className="mt-2 grid grid-cols-[minmax(0,1fr)] gap-4">
            {STEPS.map(([t, d], i) => (
              <li key={t} className="flex gap-3">
                <span className="bg-card grid size-7 shrink-0 place-items-center rounded-full border text-xs font-semibold">{i + 1}</span>
                <div>
                  <p className="font-medium">{t}</p>
                  <p className="text-muted-foreground text-sm">{d}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>
        <Card className="md:sticky md:top-8">
          <CardHeader>
            <CardTitle className="text-xl">{mode === 'login' ? 'Welcome back' : 'Create your account'}</CardTitle>
            <CardDescription>{mode === 'login' ? 'Sign in to manage your calendars.' : 'Free to use. Subscribers never need an account.'}</CardDescription>
          </CardHeader>
          <CardContent>
            <form className="grid grid-cols-[minmax(0,1fr)] gap-4" onSubmit={submit} noValidate>
              {mode === 'register' ? (
                <Field label="Your name" htmlFor="name" hint="Shown as the publisher on your share pages.">
                  <Input id="name" name="name" autoComplete="name" placeholder="e.g. Ms. Wang" value={form.name} onChange={set('name')} />
                </Field>
              ) : null}
              <Field label="Email" htmlFor="email">
                <Input id="email" name="email" type="email" autoComplete="email" required value={form.email} onChange={set('email')} />
              </Field>
              <Field label="Password" htmlFor="password" hint={mode === 'register' ? 'At least 8 characters.' : undefined}>
                <Input id="password" name="password" type="password" autoComplete={mode === 'login' ? 'current-password' : 'new-password'} required value={form.password} onChange={set('password')} />
              </Field>
              <FormError>{err}</FormError>
              <Button type="submit" size="lg" disabled={busy}>
                {busy ? (mode === 'login' ? 'Signing in…' : 'Creating account…') : mode === 'login' ? 'Sign in' : 'Create account'}
              </Button>
              <p className="text-muted-foreground text-center text-sm">
                {mode === 'login' ? 'New here? ' : 'Already have an account? '}
                <button type="button" className="text-primary font-medium underline-offset-4 hover:underline" onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setErr(''); }}>
                  {mode === 'login' ? 'Create an account' : 'Sign in'}
                </button>
              </p>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
